import { enableProdMode, isDevMode, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';

import { environment } from './environments/environment';
import { AppConfig } from './app/appconfig';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TokenInterceptor } from './app/route/token.interceptor';
import { bootstrapApplication } from '@angular/platform-browser';
import { RouteModule } from './app/route/route.module';
import { provideTranslateService } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { AppComponent } from './app/app.component';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideQueryClientOptions } from '@ngneat/query';
import { CustomTooltipDefaults } from './app/shared/models/custom-tooltip-defaults';
import { provideLiveFormsRouting } from './app/liveformsweb.routes';
import { provideIndexedDb, DBConfig } from 'ngx-indexed-db';
import { HttpLoadingInterceptorService } from './app/shell/http-loading-interceptor.service';

if (environment.production) {
  enableProdMode();
}
console.log(`Running in ${!isDevMode() ? 'production' : 'development'} mode.`);

const indexDbConfig: DBConfig = {
  name: 'LiveFormsDB',
  version: 11,
  objectStoresMeta: [
    {
      store: 'offlineOrders',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'sourceType', keypath: 'sourceType', options: { unique: false } },
        { name: 'updatedAt', keypath: 'updatedAt', options: { unique: false } },
        { name: 'form', keypath: 'form', options: { unique: false } },
        { name: 'attachedResources', keypath: 'attachedResources', options: { unique: false } },
        { name: 'isTemporaryOffline', keypath: 'isTemporaryOffline', options: { unique: false } }
      ]
    },
    {
      store: 'offline-order-resources',
      storeConfig: { keyPath: 'fileId', autoIncrement: false },
      storeSchema: [
        { name: 'data', keypath: 'data', options: { unique: false } },
        { name: 'state', keypath: 'state', options: { unique: false } },
        { name: 'saveId', keypath: 'saveId', options: { unique: false } },
        { name: 'mimeType', keypath: 'mimeType', options: { unique: false } },
        { name: 'orderId', keypath: 'orderId', options: { unique: false } }
      ]
    },
    {
      store: 'userInformation',
      storeConfig: { keyPath: 'userID', autoIncrement: false },
      storeSchema: [
        { name: 'displayName', keypath: 'displayName', options: { unique: false } },
        { name: 'department', keypath: 'department', options: { unique: false } },
        { name: 'userGroups', keypath: 'userGroups', options: { unique: false } }
      ]
    },
    {
      store: 'userSettings',
      storeConfig: { keyPath: 'userID', autoIncrement: false },
      storeSchema: [
        { name: 'userName', keypath: 'userName', options: { unique: false } },
        { name: 'displayName', keypath: 'displayName', options: { unique: false } },
        { name: 'userGroups', keypath: 'userGroups', options: { unique: false } },
        { name: 'items', keypath: 'items', options: { unique: false } }
      ]
    },
    {
      store: 'conflictedOrders',
      storeConfig: { keyPath: 'orderId', autoIncrement: false },
      storeSchema: []
    }
  ]
};

bootstrapApplication(AppComponent, {
  providers: [
    provideLiveFormsRouting(),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' })
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    // The custom service worker assumes production mode where Angular and API share
    // the same origin. In development they run on different ports, so the SW's
    // cross-origin check would incorrectly block API requests. Keep disabled in dev.
    provideServiceWorker('custom-sw.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately'
    }),
    importProvidersFrom(RouteModule.forRoot()),
    provideAppInitializer(() => {
      const initializerFn = (
        (config: AppConfig): (() => Promise<void>) =>
        () =>
          config.loadConfig()
      )(inject(AppConfig));
      return initializerFn();
    }),
    { provide: HTTP_INTERCEPTORS, useClass: HttpLoadingInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: CustomTooltipDefaults },
    provideQueryClientOptions({
      defaultOptions: {
        queries: {
          staleTime: 3600
        }
      }
    }),
    provideIndexedDb(indexDbConfig)
  ]
}).catch((err) => console.error(err));
